import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import HorizonLongHoldPanel from "./components/HorizonLongHoldPanel";
import CoinDetailsModal from "./components/CoinDetailsModal";
import SnapshotOverlay from "./SnapshotOverlay";
import TradingModeSettings from "./TradingModeSettings";
import LiveTradeVisualizer from "./components/LiveTradeVisualizer";
import WeightGraph from "./components/WeightGraph";
import SafetyControls from "./components/SafetyControls";
import EngineAllocationPanel from "./components/EngineAllocationPanel";
import WalletTotalsPanel from "./components/WalletTotalsPanel";
import DreamNebulaPanel from "./components/DreamNebulaPanel";
import HorizonPanel from "./components/HorizonPanel";
import ScalpPanel from "./components/ScalpPanel";
import TradeFailurePanel from "./components/TradeFailurePanel";
import LiveModeStatusPanel from "./components/LiveModeStatusPanel";
import LiveAccountLayoutPanel, { getLayoutFromReadiness } from "./components/LiveAccountLayoutPanel";
import { summarizeLiveAccountLayout } from "./components/LiveAccountLayoutSummary";
import LiveExposureOverview from "./components/LiveExposureOverview";
import { fetchAiReviewStatus, safeArray, safeObj } from "./utils/api";
import { normalizeSignalLogResponse } from "./utils/signalLog";
import { fetchJson, subscribeApiWarnings } from "./lib/api";
import { apiBase } from "./lib/apiBase";
import { useRuntimeConfig, deriveEngineCoins, normalizeSymbols } from "./lib/runtimeConfig";
import { usePrices } from "./lib/prices";
import { buildGaugeTelemetry, mergeGaugeSources } from "./lib/gaugeTelemetry";
import { useAuth } from "./lib/AuthContext";
import AlertBanner from "./components/AlertBanner";
import SystemStatusPanel from "./components/SystemStatusPanel";
import WhaleActivityPanel from "./components/WhaleActivityPanel";
import useWhaleSummary from "./hooks/useWhaleSummary";
import StrategyAnalytics from "./pages/StrategyAnalytics";
import MCPReviewPanel from "./components/MCPReviewPanel";

// NebulaDashboard is a simplified dashboard layout inspired by the
// "Nebula Trade UI Concept". It organizes content into a full width
// Dream panel and two strategy columns (Horizon and Scalping).


const TRADE_STATUSES = new Set(["PENDING", "EXECUTED", "SIMULATED", "BLOCKED", "SKIPPED", "FAILED"]);

function finiteNumber(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === "string" && value.trim() === "") return null;
    const num = typeof value === "number" ? value : Number(value);
    return Number.isFinite(num) ? num : null;
}

function normalizeTradeMode(value) {
    if (typeof value !== "string" || !value.trim()) return "unknown";
    const mode = value.trim().toLowerCase();
    if (["paper", "live", "dry_run", "dry-run", "dry", "sim", "simulation", "simulated"].includes(mode)) {
        return mode === "dry" || mode === "dry-run" ? "dry_run" : mode === "simulation" || mode === "simulated" ? "sim" : mode;
    }
    return "unknown";
}

function modeLabel(value) {
    const mode = normalizeTradeMode(value);
    if (mode === "paper") return "Paper";
    if (mode === "live") return "Live";
    if (mode === "dry_run") return "Dry-run";
    if (mode === "sim") return "Simulated";
    return "Unknown";
}


const HEADER_STATUS_MAX_AGE_MS = 5 * 60 * 1000;
const HEADER_STATUS_FUTURE_SKEW_MS = 60 * 1000;

function hasFreshHeaderTimestamp(timestamp) {
    if (typeof timestamp !== "string" || !timestamp.trim()) return false;
    const parsed = Date.parse(timestamp);
    if (!Number.isFinite(parsed)) return false;
    const ageMs = Date.now() - parsed;
    return ageMs >= -HEADER_STATUS_FUTURE_SKEW_MS && ageMs <= HEADER_STATUS_MAX_AGE_MS;
}

function formatDiagnosticTime(ts) {
    if (typeof ts !== "string" || !ts.trim()) return "unknown time";
    return ts;
}

function collectBooleanReadinessAliases(readiness) {
    return ["ready", "ok", "status"]
        .filter((field) => typeof readiness?.[field] === "boolean")
        .map((field) => readiness[field]);
}

function headerLiveStatus(readiness) {
    if (!readiness || typeof readiness !== "object" || Array.isArray(readiness)) {
        return {
            dotClass: "text-red-400",
            label: "Live unavailable",
            detail: "Read-only live readiness status: checks are unavailable; live mode remains API-gated.",
        };
    }

    const aliases = collectBooleanReadinessAliases(readiness);
    const blockers = Array.isArray(readiness.blocking_reasons)
        ? readiness.blocking_reasons.filter(Boolean)
        : Array.isArray(readiness.reasons)
          ? readiness.reasons.filter(Boolean)
          : null;
    const hasLiveRequested = typeof readiness.live_requested === "boolean";
    const fresh = hasFreshHeaderTimestamp(readiness.timestamp);
    const aliasesAgree = aliases.length > 0 && aliases.every((value) => value === aliases[0]);

    if (!Array.isArray(blockers) || !hasLiveRequested || !fresh || !aliasesAgree) {
        return {
            dotClass: "text-red-400",
            label: "Live unavailable",
            detail: "Read-only live readiness status: checks are missing, stale, malformed, or conflicting; live mode remains API-gated.",
        };
    }

    if (aliases.every(Boolean) && readiness.live_requested === true && blockers.length === 0) {
        return {
            dotClass: "text-green-400",
            label: "Live checks passing",
            detail: "Read-only live readiness status: no live blockers are reported; live mode remains API-gated.",
        };
    }

    return {
        dotClass: "text-amber-300",
        label: readiness.live_requested ? "Live blocked" : "Live offline",
        detail: "Read-only live readiness status: checks do not allow live enablement; live mode remains API-gated.",
    };
}

function isValidRawStatusPayload(payload) {
    return Boolean(
        payload
        && typeof payload === 'object'
        && !Array.isArray(payload)
        && payload.ok === true
        && hasFreshHeaderTimestamp(payload.timestamp),
    );
}

function headerConnectionStatus(health) {
    if (health?.apiUnavailable) {
        return {
            dotClass: "text-red-400",
            label: "API unavailable",
            detail: "Read-only connection status: the status endpoint is unavailable, so dashboard API-process reachability cannot be confirmed.",
        };
    }

    if (!health || typeof health !== "object" || Array.isArray(health) || !hasFreshHeaderTimestamp(health.timestamp)) {
        return {
            dotClass: "text-red-400",
            label: "Connection unavailable",
            detail: "Read-only connection status: backend status evidence is missing, malformed, or stale.",
        };
    }

    if (health.status === "ok" || health.ok === true) {
        return {
            dotClass: "text-green-400",
            label: "Backend connected",
            detail: "Read-only connection status: fresh raw backend status confirms dashboard connectivity; this control does not connect accounts or wallets.",
        };
    }

    if (typeof health.status === "string" && health.status.trim()) {
        return {
            dotClass: "text-amber-300",
            label: `Backend ${health.status}`,
            detail: "Read-only connection status: backend reports " + health.status + ", which does not confirm a healthy connection.",
        };
    }

    return {
        dotClass: "text-red-400",
        label: "Connection unavailable",
        detail: "Read-only connection status: backend status does not confirm a connection.",
    };
}

function normalizeTradeStatus(value) {
    if (typeof value !== "string") return null;
    const status = value.trim().toUpperCase();
    return TRADE_STATUSES.has(status) ? status : null;
}

function summarizeLabels(values, formatter = (v) => v) {
    const counts = new Map();
    values.forEach((value) => {
        const label = formatter(value);
        counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
        .map(([label, count]) => `${label}${count > 1 ? ` ×${count}` : ""}`)
        .join(", ");
}

function normalizeLegacyTradeRows(summary) {
    if (!Array.isArray(summary)) return null;
    return summary.map((row) => {
        if (!row || typeof row !== "object" || typeof row.coin !== "string" || !row.coin.trim()) {
            throw new Error("malformed trade summary row");
        }
        const trades = finiteNumber(row.trades ?? row.total_trades);
        const wins = finiteNumber(row.wins);
        const losses = finiteNumber(row.losses);
        const roi = finiteNumber(row.roi ?? row.realized_return_pct ?? row.return_pct);
        const winRate = finiteNumber(row.win_rate ?? row.winRate);
        if ([trades, wins, losses, roi, winRate].some((value) => value === null)) {
            throw new Error("malformed trade summary metrics");
        }
        const status = normalizeTradeStatus(row.status);
        const mode = normalizeTradeMode(row.mode);
        return {
            coin: row.coin.trim().toUpperCase(),
            trades,
            wins,
            losses,
            draws: finiteNumber(row.draws) ?? 0,
            roi,
            pnl: finiteNumber(row.pnl),
            win_rate: winRate <= 1 && winRate >= 0 ? winRate * 100 : winRate,
            delta: finiteNumber(row.delta),
            modes: [mode],
            mode_label: modeLabel(mode),
            statuses: status ? [status] : [],
            status_label: status || "—",
        };
    });
}

function normalizeTradeSummaryAggregate(data) {
    // The API guarantees these top-level compatibility fields are finalized from
    // one selected source (canonical or legacy_fallback). Canonical diagnostic
    // counts remain additive metadata and must not override the displayed set.
    const totalTrades = finiteNumber(data?.total_trades);
    const wins = finiteNumber(data?.wins);
    const losses = finiteNumber(data?.losses);
    const draws = finiteNumber(data?.draws);
    const winRate = finiteNumber(data?.win_rate);
    const realizedReturn = finiteNumber(data?.realized_return_pct);
    if ([totalTrades, wins, losses, draws, winRate, realizedReturn].some((value) => value === null)) {
        throw new Error("malformed trade summary aggregate");
    }
    return {
        total_trades: totalTrades,
        wins,
        losses,
        draws,
        win_rate: winRate <= 1 && winRate >= 0 ? winRate * 100 : winRate,
        realized_return_pct: realizedReturn,
        clean_measurement_readiness: data?.clean_measurement_readiness,
        metrics_contract_version: data?.metrics_contract_version,
        trade_summary_source: data?.trade_summary_source,
        sample_source: data?.sample_source,
        selected_evidence_label: data?.selected_evidence_label,
        source_counts: data?.source_counts,
        canonical_metric_eligible_closed_trades: data?.canonical_metric_eligible_closed_trades ?? data?.closed_trade_count ?? data?.metric_eligible_outcomes,
        legacy_reconstructed_sample_count: data?.legacy_reconstructed_sample_count,
        closed_trade_count: data?.closed_trade_count,
        metric_eligible_outcomes: data?.metric_eligible_outcomes,
        unique_outcomes: data?.unique_outcomes,
        raw_outcome_rows: data?.raw_outcome_rows ?? data?.raw_rows_seen,
        duplicate_outcomes_dropped: data?.duplicate_outcomes_dropped,
        dust_outcomes: data?.dust_outcomes,
        suspected_batch_outcomes: data?.suspected_batch_outcomes,
        signal_linkage_contradictions: data?.signal_linkage_contradictions,
        excluded_outcomes: data?.excluded_outcomes,
        performance_scope: data?.performance_scope,
        historical_context: data?.historical_context,
    };
}

function normalizeCurrentTradeSummary(data) {
    if (!data || typeof data !== "object") throw new Error("missing trade summary payload");
    const legacyRows = normalizeLegacyTradeRows(data.summary);
    if (legacyRows) return legacyRows;

    const totalTrades = finiteNumber(data.total_trades);
    const wins = finiteNumber(data.wins);
    const losses = finiteNumber(data.losses);
    const draws = finiteNumber(data.draws);
    const winRate = finiteNumber(data.win_rate);
    const realizedReturn = finiteNumber(data.realized_return_pct);
    const samples = data.samples;
    if (
        !Array.isArray(samples)
        || [totalTrades, wins, losses, draws, winRate, realizedReturn].some((value) => value === null)
    ) {
        throw new Error("malformed trade summary payload");
    }
    if (samples.length === 0) return [];

    const byCoin = new Map();
    samples.forEach((sample) => {
        if (!sample || typeof sample !== "object" || typeof sample.coin !== "string" || !sample.coin.trim()) {
            throw new Error("malformed trade summary sample");
        }
        const coin = sample.coin.trim().toUpperCase();
        const ret = finiteNumber(sample.return_pct);
        const pnl = finiteNumber(sample.pnl);
        const bucket = byCoin.get(coin) || {
            coin,
            trades: 0,
            wins: 0,
            losses: 0,
            draws: 0,
            roi: null,
            pnl: 0,
            hasPnl: false,
            modes: [],
            statuses: [],
        };
        bucket.trades += 1;
        if (ret === null) {
            bucket.draws += 1;
        } else {
            if (ret > 0) bucket.wins += 1;
            else if (ret < 0) bucket.losses += 1;
            else bucket.draws += 1;
        }
        if (pnl !== null) {
            bucket.pnl += pnl;
            bucket.hasPnl = true;
        }
        bucket.modes.push(normalizeTradeMode(sample.mode ?? data.mode));
        const status = normalizeTradeStatus(sample.status);
        if (status) bucket.statuses.push(status);
        byCoin.set(coin, bucket);
    });

    return Array.from(byCoin.values()).map((bucket) => ({
        ...bucket,
        pnl: bucket.hasPnl ? bucket.pnl : null,
        win_rate: bucket.trades ? (bucket.wins / bucket.trades) * 100 : 0,
        delta: null,
        mode_label: summarizeLabels(bucket.modes, modeLabel),
        status_label: bucket.statuses.length ? summarizeLabels(bucket.statuses) : "—",
    }));
}

function buildCoins(
    settings,
    priceMap = new Map(),
    mcpRes = {},
    healthRes = {},
    tradeRes = {},
) {
    return Object.keys(settings).map((key) => {
        const cfg = settings[key] || {};
        const symbol = (cfg.symbol || key).toUpperCase();
        const trades = Array.isArray(tradeRes)
            ? tradeRes.filter((t) => t.coin?.toLowerCase() === key)
            : [];
        return {
            // Use coin slug as id so components can derive abbreviations
            id: key,
            slug: key,
            symbol,
            enabledModules: cfg.enabled_modules || [],
            price: priceMap.get(symbol),
            trend: mcpRes?.[key]?.trend,
            mcp: mcpRes?.[key]?.mcp,
            signalStrength: mcpRes?.[key]?.signal_strength,
            thinkerScore: mcpRes?.[key]?.thinker_score,
            mcpConfidence: mcpRes?.[key]?.mcp_confidence,
            suppression: healthRes?.suppression_ratio?.[key],
            alerts: mcpRes?.[key]?.alerts || [],
            roi: tradeRes?.[key]?.roi ?? trades.slice(-1)[0]?.roi,
            lastTrade: trades.slice(-1)[0]?.timestamp,
        };
    });
}


function isMeaningfulStrategyHealthCoinRow(row) {
    if (!row || typeof row !== "object" || Array.isArray(row)) return false;
    const status = typeof row.status === "string" ? row.status.trim().toLowerCase() : "";
    if (status && !["no_data", "no_data_yet", "unknown"].includes(status)) return true;
    return ["health_score", "win_rate", "total_win_rate", "suppression_ratio"].some((field) => row[field] !== undefined && row[field] !== null);
}

function strategyHealthSourceStatus(res) {
    if (!res) return "unknown";
    if (!res.ok) return "unavailable";
    const data = res.data;
    if (!data || typeof data !== "object" || Array.isArray(data)) return "malformed";

    if (Array.isArray(data.insights) && data.insights.length > 0) {
        return "ok";
    }

    const positionFields = ["horizon_positions", "dream_positions", "scalping_positions"];
    if (positionFields.some((field) => Array.isArray(data[field]) && data[field].length > 0)) {
        return "ok";
    }

    const coinMap = data.coins;
    if (coinMap && typeof coinMap === "object" && !Array.isArray(coinMap)) {
        const coinRows = Object.values(coinMap);
        if (coinRows.some(isMeaningfulStrategyHealthCoinRow)) return "ok";
    }

    if (data.health_score !== undefined && data.health_score !== null) return "ok";
    if (data.total_win_rate !== undefined && data.total_win_rate !== null) return "ok";

    return "empty";
}

function equalEngineCoins(a = {}, b = {}) {
    const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
    for (const key of keys) {
        const first = Array.isArray(a?.[key]) ? a[key] : [];
        const second = Array.isArray(b?.[key]) ? b[key] : [];
        if (first.length !== second.length) return false;
        for (let i = 0; i < first.length; i += 1) {
            if (first[i] !== second[i]) return false;
        }
    }
    return true;
}

// `/engine_coins` is canonical endpoint data, but its coins may be canonical
// symbols or supported coin slugs. Normalize through runtimeConfig's shared
// symbol resolver before panel lookup and gauge-key construction. A malformed
// response must not erase the trusted runtime-config fallback.
export function normalizeCanonicalEngineCoins(data, fallback) {
    if (!data || typeof data !== "object" || Array.isArray(data)) return fallback;
    const scalp = Array.isArray(data.scalp)
        ? data.scalp
        : Array.isArray(data.scalping)
          ? data.scalping
          : Array.isArray(data.scalping_engine)
            ? data.scalping_engine
            : null;
    if (!Array.isArray(data.dream) || !Array.isArray(data.horizon) || !scalp) return fallback;
    return {
        dream: normalizeSymbols(data.dream),
        horizon: normalizeSymbols(data.horizon),
        scalp: normalizeSymbols(scalp),
    };
}
export default function NebulaDashboard({ adminEnabled: adminEnabledProp }) {
    const { config, error: configError, reload: reloadConfig } = useRuntimeConfig();
    const { prices, lastUpdated, initialLoading, refreshing, error: priceError } = usePrices({ intervalMs: 10000 });
    const [coins, setCoins] = useState([]);
    const [engineCoins, setEngineCoins] = useState(() => deriveEngineCoins(config));
    const [gaugeSources, setGaugeSources] = useState({});
    const priceMap = useMemo(() => new Map(Object.entries(prices || {})), [prices]);
    const priceObject = useMemo(() => Object.fromEntries(priceMap || new Map()), [priceMap]);
    const priceMapRef = useRef(priceMap);

    useEffect(() => {
        priceMapRef.current = priceMap;
    }, [priceMap]);

    useEffect(() => {
        // Price polling is intentionally a partial source update; it must never
        // replace confidence, PnL, position, or performance telemetry.
        setGaugeSources((previous) => mergeGaugeSources(previous, { prices, priceUpdatedAt: lastUpdated }));
    }, [prices, lastUpdated]);

    useEffect(() => {
        const next = deriveEngineCoins(config);
        setEngineCoins((prev) => (equalEngineCoins(prev, next) ? prev : next));
    }, [config]);
    const [backendHealth, setBackendHealth] = useState(null);
    const [strategyHealthStatus, setStrategyHealthStatus] = useState("unknown");
    const [selectedCoin, setSelectedCoin] = useState(null);
    const [snapshotOpen, setSnapshotOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const compact = false;
    const adminEnabled = adminEnabledProp ?? (globalThis.__IMPORT_META_ENV__?.VITE_ADMIN_MODE === "true");
    const [showAdmin, setShowAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [tradeSummary, setTradeSummary] = useState([]);
    const [tradeSummaryAggregate, setTradeSummaryAggregate] = useState(null);
    const [tradeSummaryStatus, setTradeSummaryStatus] = useState("idle");
    const [replayCoin, setReplayCoin] = useState(null);
    const [aiReviewStatus, setAiReviewStatus] = useState(null);
    const [aiReviewError, setAiReviewError] = useState(null);
    const [tradeFailures, setTradeFailures] = useState({});
    const [engineDiagnostics, setEngineDiagnostics] = useState({ dream: {}, scalp: {}, horizon: {} });
    const [tradeDiagnosticsCollapsed, setTradeDiagnosticsCollapsed] = useState(true);
    const [apiWarning, setApiWarning] = useState(null);
    const [activeTab, setActiveTab] = useState(null);
    const [horizonPositions, setHorizonPositions] = useState([]);
    const [horizonActionMessage, setHorizonActionMessage] = useState("");
    const [horizonPendingIds, setHorizonPendingIds] = useState(() => new Set());
    const [liveReadiness, setLiveReadiness] = useState(null);
    const [binanceRulesStatus, setBinanceRulesStatus] = useState(null);
    const [liveExposure, setLiveExposure] = useState([]);
    const [liveAccountCollapsed, setLiveAccountCollapsed] = useState(true);
    const [alertState, setAlertState] = useState({ alerts: [], counts: {}, email: null });
    const [alertPollDelay, setAlertPollDelay] = useState(10000);
    const { authenticated, markUnauthorized } = useAuth();
    const emptyAiCounts = useMemo(
        () => ({ pending: 0, applied: 0, ignored: 0, live_pending: 0, live_applied: 0 }),
        [],
    );

    const normalizeAiCounts = useCallback(
        (counts) => ({
            pending: counts?.pending ?? 0,
            applied: counts?.applied ?? 0,
            ignored: counts?.ignored ?? 0,
            live_pending: counts?.live_pending ?? 0,
            live_applied: counts?.live_applied ?? 0,
        }),
        [],
    );

    useEffect(() => {
        const list = Array.isArray(config?.coins) ? config.coins : [];
        const mapping = Object.fromEntries(list.map((c) => [c.slug, c]));
        setCoins(buildCoins(mapping, priceMap));
    }, [config, priceMap]);

    const uniqueCoins = useMemo(() => {
        const seen = new Set();
        return coins.filter((coin) => {
            const key = (coin?.slug || coin?.symbol || coin?.id || "")
                .toString()
                .toLowerCase()
                .replace(/[^a-z0-9]/g, "");
            if (!key) return false;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [coins]);

    const gaugeTelemetry = useMemo(() => {
        const list = buildGaugeTelemetry({ ...gaugeSources, engineCoins });
        return Object.fromEntries(list.map((gauge) => [gauge.key, gauge]));
    }, [gaugeSources, engineCoins]);

    const coinMetaMap = useMemo(() => {
        const map = new Map();
        coins.forEach((coin) => {
            const slugKey = coin?.slug ? coin.slug.toLowerCase() : null;
            const symbolKey = coin?.symbol ? coin.symbol.toUpperCase() : null;
            if (slugKey) map.set(slugKey, coin);
            if (symbolKey) map.set(symbolKey, coin);
        });
        return map;
    }, [coins]);

    const liveAccountLayout = useMemo(() => getLayoutFromReadiness(liveReadiness), [liveReadiness]);
    const liveAccountSummary = useMemo(
        () => summarizeLiveAccountLayout(liveAccountLayout),
        [liveAccountLayout],
    );

    const toggleLiveAccountLayout = useCallback(
        () => setLiveAccountCollapsed((prev) => !prev),
        [],
    );

    const handleAuthFailure = useCallback(() => {
        markUnauthorized();
    }, [markUnauthorized]);
    const whaleSummary = useWhaleSummary({ authenticated, onUnauthorized: handleAuthFailure });

    const isMountedRef = useRef(true);

    useEffect(() => () => { isMountedRef.current = false; }, []);

    const loadAlerts = useCallback(async () => {
        if (!authenticated) return;
        const res = await fetchJson("/api/alerts");
        if (!isMountedRef.current) return;
        if (res.ok && res.data) {
            setAlertState(res.data);
            setAlertPollDelay(10000);
        } else {
            setAlertPollDelay((delay) => Math.min(delay * 2, 60000));
        }
    }, [authenticated]);

    useEffect(() => {
        let cancelled = false;
        let timer;
        const tick = async () => {
            await loadAlerts();
            if (cancelled) return;
            timer = setTimeout(tick, alertPollDelay);
        };
        tick();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [alertPollDelay, loadAlerts]);

    const handleAcknowledgeAlerts = useCallback(async () => {
        if (!authenticated || !adminEnabled) return;
        const res = await fetchJson("/api/alerts/ack", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ all: true }),
        });
        if (res.ok && res.data) {
            setAlertState(res.data);
        }
    }, [adminEnabled, authenticated]);

    const loadDashboard = useCallback(
        async (options = {}) => {
            if (!authenticated || !isMountedRef.current) return;
            const { silent = false } = options;
            const list = Array.isArray(config?.coins) ? config.coins : [];
            const fallback = Object.fromEntries(list.map((c) => [c.slug, c]));
            try {
                if (!silent) setLoading(true);
                const [healthRes, engineRes, tradingModeRes, runtimeModeRes, walletRes, thinkerRes, strategyScoresRes, tradeSummaryRes, backendHealthRes] = await Promise.all([
                    fetchJson("/strategy_health", { canonical: true }).catch(() => ({ ok: false, data: null })),
                    fetchJson("/engine_coins", { canonical: true }).catch(() => ({ ok: false, data: null })),
                    fetchJson("/trading_mode_status", { canonical: true }).catch(() => ({ ok: false, data: null })),
                    fetchJson("/runtime_mode", { canonical: true }).catch(() => ({ ok: false, data: null })),
                    fetchJson("/wallet", { canonical: true }).catch(() => ({ ok: false, data: null })),
                    fetchJson("/thinker", { canonical: true }).catch(() => ({ ok: false, data: null })),
                    fetchJson("/strategy_scores", { canonical: true }).catch(() => ({ ok: false, data: null })),
                    fetchJson("/trade_summary", { canonical: true }).catch(() => ({ ok: false, data: null })),
                    // Connectivity is based solely on the raw status contract. Do
                    // not substitute heartbeat health here: heartbeat can be
                    // intentionally unavailable when log access is gated while the
                    // API process itself remains reachable.
                    fetchJson(apiBase("status"), { unwrapContract: false }).catch(() => ({ ok: false, data: null, status: "network_error" })),
                ]);

                if ([healthRes, engineRes, tradingModeRes, runtimeModeRes, walletRes, thinkerRes, strategyScoresRes, tradeSummaryRes, backendHealthRes].some((res) => res?.unauth)) {
                    handleAuthFailure();
                    return;
                }
                if (!isMountedRef.current) return;

                setStrategyHealthStatus(strategyHealthSourceStatus(healthRes));
                const healthData = healthRes?.ok && healthRes.data && typeof healthRes.data === "object" && !Array.isArray(healthRes.data) ? healthRes.data : {};
                const fallbackEngineCoins = deriveEngineCoins(config);
                const canonicalEngineCoins = engineRes?.ok
                    ? normalizeCanonicalEngineCoins(engineRes.data, fallbackEngineCoins)
                    : fallbackEngineCoins;
                setEngineCoins((previous) => (equalEngineCoins(previous, canonicalEngineCoins) ? previous : canonicalEngineCoins));
                const modeSourceObservedAt = new Date().toISOString();
                setGaugeSources((previous) => mergeGaugeSources(previous, {
                    engineCoins: engineRes?.ok ? engineRes.data : undefined,
                    strategyHealth: healthRes?.ok ? healthData : undefined,
                    tradingModeStatus: tradingModeRes?.ok ? tradingModeRes.data : undefined,
                    runtimeMode: runtimeModeRes?.ok ? runtimeModeRes.data : undefined,
                    wallet: walletRes?.ok ? walletRes.data : undefined,
                    thinker: thinkerRes?.ok ? thinkerRes.data : undefined,
                    strategyScores: strategyScoresRes?.ok ? strategyScoresRes.data : undefined,
                    tradeSummary: tradeSummaryRes?.ok ? tradeSummaryRes.data : undefined,
                    status: backendHealthRes?.ok ? backendHealthRes.data : undefined,
                }, {
                    sourceObservedAt: {
                        tradingModeStatus: modeSourceObservedAt,
                        runtimeMode: modeSourceObservedAt,
                        wallet: modeSourceObservedAt,
                    },
                }));
                const coinsData = buildCoins(fallback, priceMapRef.current, {}, safeObj(healthData), {});
                setCoins(coinsData);
                setHorizonPositions(safeArray(healthData?.horizon_positions));
                setTradeFailures(healthData?.trade_failures || {});
                setEngineDiagnostics(healthData?.engine_diagnostics || { dream: {}, scalp: {}, horizon: {} });
                setLiveExposure(safeArray(healthData?.live_exposure));
                setBinanceRulesStatus(healthData?.binance_rules || null);
                const readinessPayload = tradingModeRes?.data?.live_readiness;
                setLiveReadiness(readinessPayload && typeof readinessPayload === "object" && !Array.isArray(readinessPayload) ? readinessPayload : null);
                const healthPayload = backendHealthRes?.data;
                setBackendHealth(backendHealthRes?.ok && isValidRawStatusPayload(healthPayload)
                    ? healthPayload : { apiUnavailable: true, status: backendHealthRes?.status || "invalid_status" });
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Failed to load dashboard data", err);
                if (isMountedRef.current) setHorizonPositions([]);
            } finally {
                if (!silent && isMountedRef.current) setLoading(false);
            }
        },
        [authenticated, config, handleAuthFailure],
    );

    useEffect(() => {
        if (!authenticated) return undefined;
        let active = true;
        loadDashboard({ silent: false });
        const intervalId = window.setInterval(() => {
            if (active) return loadDashboard({ silent: true });
            return undefined;
        }, 10000);
        return () => {
            active = false;
            window.clearInterval(intervalId);
        };
    }, [authenticated, loadDashboard]);

    
    const loadAiReviewStatus = useCallback(
        async () => {
            if (!authenticated || !isMountedRef.current) return;
            try {
                const res = await fetchAiReviewStatus();
                if (!isMountedRef.current) return;
                if (res?.unauth) {
                    handleAuthFailure();
                    return;
                }
                if (!res?.ok) {
                    const data = res?.data || null;
                    const fallback = data
                        ? { ...data, counts: normalizeAiCounts(data.counts), status: data.status || "temporarily_unavailable" }
                        : null;
                    setAiReviewStatus(fallback);
                    setAiReviewError("unavailable");
                    return;
                }
                const data = res?.data || {};
                const normalized = {
                    ...data,
                    counts: normalizeAiCounts(data.counts),
                    status: data.status || (data.message === "no_data_yet" ? "no_data_yet" : "ok"),
                    message: data.message,
                };
                setAiReviewStatus(normalized);
                setAiReviewError(data.message === "no_data_yet" ? "no_data_yet" : null);
            } catch {
                if (!isMountedRef.current) return;
                setAiReviewStatus(null);
                setAiReviewError("unavailable");
            }
        },
        [authenticated, handleAuthFailure, normalizeAiCounts],
    );
    useEffect(() => {
        if (!authenticated) return undefined;
        let active = true;
        loadAiReviewStatus();
        const id = window.setInterval(() => {
            if (active) loadAiReviewStatus();
        }, 12000);
        return () => {
            active = false;
            clearInterval(id);
        };
    }, [authenticated, loadAiReviewStatus]);

    useEffect(() => {
        const unsubscribe = subscribeApiWarnings((warning) => {
            setApiWarning(warning);
        });
        return () => unsubscribe();
    }, []);

    
    useEffect(() => {
        if (!authenticated) return undefined;
        let active = true;
        async function load() {
            try {
                const res = await fetchJson(apiBase("trade_summary"));
                if (!active) return;
                if (res?.unauth) {
                    setTradeSummary([]);
                    setTradeSummaryAggregate(null);
                    setTradeSummaryStatus("unauthorized");
                    handleAuthFailure();
                    return;
                }
                if (!res?.ok) {
                    if (!active) return;
                    setTradeSummary([]);
                    setTradeSummaryAggregate(null);
                    const status = res?.status || res?.data?.message;
                    if (status === "no_data_yet") {
                        setTradeSummaryStatus("no_data");
                    } else if (res?.unauth) {
                        setTradeSummaryStatus("unauthorized");
                        handleAuthFailure();
                    } else {
                        setTradeSummaryStatus("error");
                    }
                    return;
                }
                if (!active) return;
                let summary;
                let aggregate = null;
                try {
                    summary = normalizeCurrentTradeSummary(res.data);
                    if (!Array.isArray(res.data?.summary)) {
                        aggregate = normalizeTradeSummaryAggregate(res.data);
                    }
                } catch {
                    setTradeSummary([]);
                    setTradeSummaryAggregate(null);
                    setTradeSummaryStatus("error");
                    return;
                }
                setTradeSummary(summary);
                setTradeSummaryAggregate(aggregate);
                setGaugeSources((previous) => mergeGaugeSources(previous, { tradeSummary: res.data }));
                if (summary.length === 0 && res.data?.message === "no_data_yet") {
                    setTradeSummaryStatus("no_data");
                } else if (summary.length === 0) {
                    setTradeSummaryStatus("empty");
                } else {
                    setTradeSummaryStatus("ready");
                }
            } catch {
                if (!active) return;
                setTradeSummary([]);
                setTradeSummaryAggregate(null);
                setTradeSummaryStatus("error");
            }
        }
        load();
        const id = setInterval(load, 15000);
        return () => {
            active = false;
            clearInterval(id);
        };
    }, [authenticated, handleAuthFailure]);

    const lastSelect = useRef(0);
    // The rendered compatibility modal remains in place while gauge components do not yet expose selection.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- retain the bounded detail-fetch path for that modal
    const handleSelectCoin = useCallback(async (coin) => {
        const now = Date.now();
        if (now - lastSelect.current < 300) return;
        lastSelect.current = now;
        const selected =
            coinMetaMap.get(String(coin?.slug || "").toLowerCase()) ||
            coinMetaMap.get(String(coin?.symbol || "").toUpperCase()) ||
            coin;

        try {
            const signalLogCoin = selected.symbol || selected.slug;
            const [signalLog, tradeLog] = await Promise.all([
                fetchJson(apiBase(`signal_log/${signalLogCoin}`)).catch(() => ({ ok: false })),
                fetchJson(apiBase(`trade_log/${selected.slug}`)).catch(() => ({ ok: false, data: [] })),
            ]);
            if (signalLog?.unauth || tradeLog?.unauth) {
                handleAuthFailure();
                return;
            }
            const normalizedSignalLog = normalizeSignalLogResponse(signalLog);
            setSelectedCoin({
                ...selected,
                signalLog: normalizedSignalLog.entries,
                signalLogStatus: normalizedSignalLog.status,
                tradeLog: safeArray(tradeLog.data),
            });
        } catch {
            setSelectedCoin({ ...selected, signalLog: [], signalLogStatus: "unavailable" });
        }
    }, [coinMetaMap, handleAuthFailure]);

    const handleHorizonApprovalToggle = useCallback(
        async (positionId, allowExit) => {
            if (!positionId) {
                setHorizonActionMessage("Failed to update Horizon approval: missing position id");
                return;
            }
            setHorizonPendingIds((prev) => new Set(prev).add(positionId));
            setHorizonActionMessage(allowExit ? "Exit approval requested…" : "Exit revocation requested…");
            try {
                const res = await fetchJson(apiBase("strategy_health"), {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ position_id: positionId, exit_permission: allowExit }),
                });
                if (res?.unauth) {
                    setHorizonActionMessage("Failed to update Horizon approval: sign in or CSRF validation required");
                    handleAuthFailure();
                    return;
                }
                const data = res?.data;
                const positions = safeArray(data?.horizon_positions);
                const confirmed =
                    res?.ok === true &&
                    data &&
                    data.ok !== false &&
                    data.status !== false &&
                    data.updated === true &&
                    data.position_id === positionId &&
                    data.exit_permission === allowExit;
                if (!confirmed || positions.length === 0) {
                    setHorizonActionMessage("Failed to update Horizon approval: backend did not confirm persisted state");
                    return;
                }
                const confirmedPosition = positions.find((p) => p.position_id === positionId);
                if (!confirmedPosition || confirmedPosition.exit_permission !== allowExit) {
                    setHorizonActionMessage("Failed to update Horizon approval: confirmed state did not match request");
                    return;
                }
                setHorizonPositions(positions);
                setHorizonActionMessage(allowExit ? "Exit approval confirmed" : "Exit revocation confirmed");
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Failed to toggle Horizon exit permission", err);
                setHorizonActionMessage("Failed to update Horizon approval: backend unavailable");
            } finally {
                setHorizonPendingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(positionId);
                    return next;
                });
            }
        },
        [handleAuthFailure],
    );


    const tabs = useMemo(
        () => [
            { key: "mcpReview", label: "MCP / AI Review" },
            { key: "adaptiveWeights", label: "Adaptive Weights" },
            { key: "engineAllocations", label: "Engine Allocations" },
            { key: "horizonLongHold", label: "Horizon Long-Hold" },
            { key: "safetyControls", label: "Safety Controls" },
            { key: "strategyAnalytics", label: "Strategy Analytics" },
            { key: "systemStatus", label: "System Status" },
            { key: "walletTotals", label: "Wallet Totals" },
            { key: "whaleActivity", label: "Whale Activity" },
        ],
        [],
    );

    const sortedTabs = useMemo(
        () => [...tabs].sort((a, b) => a.label.localeCompare(b.label)),
        [tabs],
    );

    const aiCounts = useMemo(
        () => normalizeAiCounts(aiReviewStatus?.counts || emptyAiCounts),
        [aiReviewStatus, emptyAiCounts, normalizeAiCounts],
    );

    const aiModeLabel = useMemo(() => (aiReviewStatus?.live_allowed ? "live" : "paper"), [aiReviewStatus]);
    const liveHeaderStatus = useMemo(() => headerLiveStatus(liveReadiness), [liveReadiness]);
    const connectionHeaderStatus = useMemo(() => headerConnectionStatus(backendHealth), [backendHealth]);
    const showTradeSummaryPanel = ["ready", "no_data", "empty", "error", "unauthorized"].includes(
        tradeSummaryStatus,
    );
    const sourceStatusItems = useMemo(() => [
        { label: "strategy_health", status: loading && strategyHealthStatus === "unknown" ? "unknown" : strategyHealthStatus },
        { label: "trade_summary", status: tradeSummaryStatus === "ready" ? "ok" : tradeSummaryStatus || "unknown" },
    ], [loading, strategyHealthStatus, tradeSummaryStatus]);

    const handleTabClick = useCallback((tabKey) => {
        setActiveTab((prev) => (prev === tabKey ? null : tabKey));
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (activeTab === "strategyAnalytics") {
            const url = new URL(window.location.href);
            url.pathname = "/analytics";
            window.history.replaceState({}, "", url);
        }
    }, [activeTab]);

    return (
        <>
        <div className="min-h-screen bg-gray-950 text-white">
            <div
                // Price refresh re-renders the shell; keep the wrapper stable to avoid scroll anchoring jumps.
                className="nebula-dashboard-shell relative mx-auto max-w-7xl px-6 pt-6 pb-10 space-y-6"
                data-testid="dashboard-shell"
                data-load-state={loading ? "loading" : "ready"}
            >
                {configError && (
                    <div className="text-xs bg-red-500/10 text-red-400 px-3 py-2 rounded flex items-center gap-2">
                        <span>Failed to load config.</span>
                        <button
                            type="button"
                            className="underline"
                            onClick={reloadConfig}
                        >
                            Retry
                        </button>
                    </div>
                )}
                {loading && (
                    <div data-testid="loading" className="sr-only" aria-live="polite">
                        Loading...
                    </div>
                )}
                <AlertBanner
                    alerts={alertState.alerts}
                    counts={alertState.counts}
                    emailStatus={alertState.email}
                    adminEnabled={adminEnabled && authenticated}
                    onAcknowledge={handleAcknowledgeAlerts}
                />
                {apiWarning && (
                    <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-amber-100 text-sm" data-testid="api-warning">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="text-xs uppercase tracking-wide text-amber-200">API warning</p>
                                <p className="font-semibold">
                                    API returned an invalid response for {apiWarning.path}. Some panels may be unavailable.
                                </p>
                                <p className="mt-1 text-[0.7rem] text-amber-100/80">Invalid response body omitted for operator safety.</p>
                            </div>
                            <button
                                type="button"
                                className="text-xs uppercase tracking-wide text-amber-200 hover:text-amber-50"
                                onClick={() => setApiWarning(null)}
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                )}
            <header className="space-y-4">
                <div className="flex flex-wrap items-baseline justify-between gap-4">
                    <h1 className="text-4xl font-extrabold tracking-wider">
                        <span className="text-purple-400 drop-shadow-[0_0_6px_#c084fc]">
                            NEBULA
                        </span>{" "}
                        <span className="text-cyan-400 drop-shadow-[0_0_5px_#0ff]">
                            TRADE
                        </span>
                    </h1>
                    <div className="text-xs uppercase tracking-[0.3em] text-gray-500">
                        Mission Control
                    </div>
                </div>
                <nav
                    className="flex flex-wrap items-center gap-3 text-sm text-gray-200"
                    aria-label="Dashboard controls"
                >
                    <button
                        type="button"
                        className="inline-flex cursor-not-allowed items-center gap-2 rounded-full border border-gray-800 px-3 py-1 bg-gray-900/50 text-gray-300 opacity-80"
                        disabled
                        aria-disabled="true"
                        title={liveHeaderStatus.detail}
                        data-testid="header-live-status"
                    >
                        <span className={liveHeaderStatus.dotClass}>
                            ●
                        </span>
                        <span className="font-semibold">Live</span>
                        <span className="text-xs text-gray-400">{liveHeaderStatus.label}</span>
                    </button>
                    <button
                        type="button"
                        className="inline-flex cursor-not-allowed items-center gap-2 rounded-full border border-gray-800 px-3 py-1 bg-gray-900/50 text-gray-300 opacity-80"
                        disabled
                        aria-disabled="true"
                        title={connectionHeaderStatus.detail}
                        data-testid="header-connect-status"
                    >
                        <span className={connectionHeaderStatus.dotClass}>
                            ●
                        </span>
                        <span className="font-semibold">Connect</span>
                        <span className="text-xs text-gray-400">{connectionHeaderStatus.label}</span>
                    </button>
                    <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-full border border-gray-800 px-3 py-1 bg-gray-900/50 hover:text-cyan-300"
                        onClick={() => setSnapshotOpen(true)}
                    >
                        Snapshot
                    </button>
                    <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-full border border-gray-800 px-3 py-1 bg-gray-900/50 hover:text-cyan-300"
                        onClick={() => setSettingsOpen(true)}
                    >
                        Settings
                    </button>
                    {adminEnabled && (
                        <div className="flex items-center gap-2 ml-auto">
                            <button
                                type="button"
                                className="inline-flex items-center gap-2 rounded-full border border-red-600/40 px-3 py-1 bg-red-900/40 hover:text-red-300"
                                onClick={() => setShowAdmin((v) => !v)}
                            >
                                Admin
                            </button>
                            {showAdmin && (
                                <div className="flex flex-wrap items-center gap-2" data-testid="legacy-admin-pause-controls">
                                    {["dream", "horizon", "scalp"].map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400 cursor-not-allowed opacity-70"
                                            disabled
                                            aria-disabled="true"
                                            title="Use Safety Controls for verified engine pauses"
                                        >
                                            Pause {s.charAt(0).toUpperCase() + s.slice(1)}
                                        </button>
                                    ))}
                                    <span className="text-xs text-yellow-300" role="status">
                                        Legacy header pause buttons are disabled. Use Safety Controls for verified engine pauses.
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </nav>
            </header>

            <div className="mt-4 space-y-4">
                <div>
                    <div className="flex flex-wrap gap-2 border-b border-gray-800 pb-2">
                        {sortedTabs.map((tab) => {
                            const active = activeTab === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    type="button"
                                    onClick={() => handleTabClick(tab.key)}
                                    className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide rounded-t-md border ${
                                        active
                                            ? "border-cyan-500/60 bg-gray-900 text-white"
                                            : "border-gray-800 bg-gray-900/60 text-gray-400 hover:text-white"
                                    }`}
                                    data-testid={`${tab.key}-tab`}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        {tab.label}
                                        {tab.key === "systemStatus" && whaleSummary.hasPressure && (
                                            <span
                                                className="inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-amber-400"
                                                title="Whale activity detected"
                                                aria-label="Whale activity detected"
                                                data-testid="system-status-whale-indicator"
                                            />
                                        )}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <div className="mt-3 space-y-3">
                    {activeTab && (
                        <div className="flex flex-wrap gap-2 text-[0.65rem] uppercase tracking-wide text-gray-400" data-testid="source-status-badges">
                            {sourceStatusItems.map((item) => (
                                <span key={item.label} className="rounded border border-gray-700 px-2 py-1">{item.label}: {item.status || "unknown"}</span>
                            ))}
                        </div>
                    )}
                    {activeTab === "strategyAnalytics" && <StrategyAnalytics />}
                    {activeTab === "mcpReview" && (
                        <MCPReviewPanel
                            authenticated={authenticated}
                            onUnauthorized={handleAuthFailure}
                            aiReviewStatus={aiReviewStatus}
                            aiReviewError={aiReviewError}
                            aiCounts={aiCounts}
                            aiModeLabel={aiModeLabel}
                            liveReadiness={liveReadiness}
                            tradeSummary={tradeSummary}
                            tradeSummaryAggregate={tradeSummaryAggregate}
                            tradeSummaryStatus={tradeSummaryStatus}
                            cleanReadiness={tradeSummaryAggregate?.clean_measurement_readiness}
                            showTradeSummaryPanel={showTradeSummaryPanel}
                            onReplayCoin={setReplayCoin}
                        />
                    )}
                    {activeTab === "adaptiveWeights" && (
                        <div
                            className="dashboard-section border border-gray-800 rounded-2xl bg-gray-900/70 p-4 space-y-6"
                            data-testid="adaptive-weights"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[0.65rem] uppercase tracking-wide text-gray-400">Engine allocation explorer</p>
                                    <h3 className="text-lg font-semibold text-white">Adaptive Weights</h3>
                                </div>
                                <span className="text-[0.65rem] uppercase tracking-wide text-gray-400" data-testid="adaptive-weight-count">
                                    {uniqueCoins.length} Coins
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {uniqueCoins.map((c, index) => {
                                    const identifier = c?.slug || c?.symbol || c?.id || `coin-${index}`;
                                    return <WeightGraph key={identifier} coin={identifier} />;
                                })}
                            </div>

                        </div>
                    )}

                    {activeTab === "walletTotals" && (
                        <div
                            className="dashboard-section border border-gray-800 rounded-2xl bg-gray-900/70 p-4"
                            data-testid="wallet-totals-tab"
                        >
                            <WalletTotalsPanel className="mt-0" />
                        </div>
                    )}

                    {activeTab === "engineAllocations" && (
                        <div
                            className="dashboard-section border border-gray-800 rounded-2xl bg-gray-900/70 p-4"
                            data-testid="engine-allocation-tab"
                        >
                            <EngineAllocationPanel className="mt-0" />
                        </div>
                    )}

                    {activeTab === "horizonLongHold" && (
                        <div
                            className="dashboard-section border border-gray-800 rounded-2xl bg-gray-900/70 p-4"
                            data-testid="horizon-long-hold"
                        >
                            <HorizonLongHoldPanel
                                positions={horizonPositions}
                                onToggle={handleHorizonApprovalToggle}
                                actionMessage={horizonActionMessage}
                                pendingPositionIds={horizonPendingIds}
                                className="dashboard-engine-panel"
                            />
                        </div>
                    )}

                    {activeTab === "safetyControls" && (
                        <div
                            className="dashboard-section border border-gray-800 rounded-2xl bg-gray-900/70 p-4"
                            data-testid="safety-controls"
                        >
                            <SafetyControls />
                        </div>
                    )}

                    {activeTab === "whaleActivity" && (
                        <div
                            className="dashboard-section border border-gray-800 rounded-2xl bg-gray-900/70 p-4"
                            data-testid="whale-activity-tab"
                        >
                            <WhaleActivityPanel
                                authenticated={authenticated}
                                onUnauthorized={handleAuthFailure}
                            />
                        </div>
                    )}

                    {activeTab === "systemStatus" && (
                        <div
                            className="dashboard-section border border-gray-800 rounded-2xl bg-gray-900/70 p-4"
                            data-testid="system-status"
                        >
                            <div className="mb-4">
                                <h4 className="text-base font-semibold text-white">Trade Failure Monitor</h4>
                                <p className="text-xs text-gray-400">Recent execution issues per engine</p>
                            </div>
                            <div className="grid gap-3 md:grid-cols-2">
                                <LiveModeStatusPanel readiness={liveReadiness} binanceRules={binanceRulesStatus} />
                                <LiveExposureOverview exposure={liveExposure} />
                            </div>
                            <div
                                className="mt-3 rounded-2xl border border-gray-800 bg-gray-900/70"
                                data-testid="live-account-layout-card"
                            >
                                <button
                                    type="button"
                                    onClick={toggleLiveAccountLayout}
                                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                                    data-testid="live-account-layout-toggle"
                                >
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-2">
                                            <p className="text-[0.7rem] uppercase tracking-wide text-gray-400">Live Accounts</p>
                                            <span className="text-[0.7rem] text-gray-500">Prefixes from binance_accounts.json</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-lg font-semibold text-white">Live Account Layout</h3>
                                            <span
                                                className="text-xs text-gray-400"
                                                data-testid="live-account-layout-summary"
                                            >
                                                {liveAccountSummary}
                                            </span>
                                        </div>
                                    </div>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        className={`h-4 w-4 text-gray-300 transition-transform duration-200 ${
                                            liveAccountCollapsed ? "" : "rotate-90"
                                        }`}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                                {!liveAccountCollapsed && (
                                    <div className="border-t border-gray-800 px-4 pb-4">
                                        <LiveAccountLayoutPanel
                                            readiness={liveReadiness}
                                            className="mt-2 border-0 bg-transparent p-0"
                                        />
                                    </div>
                                )}
                            </div>
                            <TradeFailurePanel failures={tradeFailures} />
                            <div className="mt-3 rounded-2xl border border-gray-800 bg-gray-900/70" data-testid="trade-diagnostics-card">
                                <button
                                    type="button"
                                    onClick={() => setTradeDiagnosticsCollapsed((s) => !s)}
                                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                                    data-testid="trade-diagnostics-toggle"
                                >
                                    <h4 className="text-base font-semibold text-white">Why Didn’t It Trade?</h4>
                                    <span className="text-xs text-gray-400">{tradeDiagnosticsCollapsed ? "Show" : "Hide"}</span>
                                </button>
                                {!tradeDiagnosticsCollapsed && (
                                    <div className="border-t border-gray-800 px-4 pb-4 text-xs">
                                        {Object.values(engineDiagnostics || {}).every((v) => !v || Object.keys(v).length === 0) ? (
                                            <div className="py-3 text-gray-400">No recent trade diagnostics yet.</div>
                                        ) : (
                                            <div className="space-y-2 py-3" data-testid="trade-diagnostics-list">
                                                {Object.entries(engineDiagnostics || {}).flatMap(([engine, coins]) =>
                                                    Object.entries(coins || {}).map(([symbol, item]) => (
                                                        <div key={`${engine}-${symbol}`} className="rounded border border-gray-800 p-2">
                                                            <div className="font-semibold text-white">{engine} · {symbol}</div>
                                                            <div className="text-gray-300">
                                                                {(item?.last_trade_decision || "unknown")} · {(item?.last_reason || "none")}
                                                            </div>
                                                            <div className="text-gray-400">{item?.last_message}</div>
                                                            <div className="text-gray-500">{formatDiagnosticTime(item?.last_checked_at)}</div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="mt-6">
                                <SystemStatusPanel />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            </div>
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-[0.65rem] uppercase tracking-wide text-gray-400">Engine status</p>
                        <h4 className="text-base font-semibold text-white">Coin Gauges</h4>
                    </div>
                    <span className="text-xs text-gray-400">{gaugeTelemetry[Object.keys(gaugeTelemetry)[0]]?.mode || "PAPER"} snapshots</span>
                </div>
                {priceError && (
                    <div className="text-xs text-amber-200 bg-amber-900/30 border border-amber-800 rounded-lg px-3 py-2" role="status">
                        Price feed unavailable — {priceError}. Check System Status for details.
                    </div>
                )}
                <div className="space-y-3">
                    <DreamNebulaPanel
                        engineCoins={engineCoins?.dream}
                        prices={priceObject}
                        compact={compact}
                        updatedAt={lastUpdated}
                        gauges={gaugeTelemetry}
                        className="dashboard-engine-panel"
                    />
                    <div className="grid gap-3 md:grid-cols-2">
                        <HorizonPanel
                            engineCoins={engineCoins?.horizon}
                            prices={priceObject}
                            compact={compact}
                            updatedAt={lastUpdated}
                            gauges={gaugeTelemetry}
                            className="dashboard-engine-panel"
                        />
                        <ScalpPanel
                            engineCoins={engineCoins?.scalp}
                            prices={priceObject}
                            compact={compact}
                            updatedAt={lastUpdated}
                            gauges={gaugeTelemetry}
                            className="dashboard-engine-panel"
                        />
                    </div>
                </div>
            </div>

            {replayCoin && (
                <div className="mb-4">
                    <LiveTradeVisualizer
                        coin={replayCoin}
                        onClose={() => setReplayCoin(null)}
                    />
                </div>
            )}
            <CoinDetailsModal
                coin={selectedCoin}
                onClose={() => setSelectedCoin(null)}
            />
            <SnapshotOverlay
                open={snapshotOpen}
                onClose={() => setSnapshotOpen(false)}
            />
            <TradingModeSettings
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
            />

            {/* Legacy overlay placeholders retained for backend tests */}
            <div className="hidden">
                <section className="mcp-overview">MCP Overview</section>
                <div id="heartbeat">heartbeat</div>
                <button type="button">Strategy Trends</button>
                <span>Adaptive Overlay</span>
                <span>Tuning Recommended</span>
                <span>Live Adjusted</span>
                <span>Stable</span>
                <span>Self-Tuning Status</span>
                <span>Confidence Calibration</span>
                <span>Suppressed Patterns</span>
                {['HBAR', 'BNB', 'ADA', 'AVAX'].map((c) => (
                    <span key={c}>{c}</span>
                ))}
            </div>
            </div>

            {/* Screen-reader only announcement so it doesn’t affect layout */}
            <span className="sr-only" role="status" aria-live="polite">
                {initialLoading ? "Loading prices…" : (refreshing ? "Updating prices…" : "")}
            </span>
        </div>
        </>
    );
}

NebulaDashboard.propTypes = {
    adminEnabled: PropTypes.bool,
};
